import styled, { css } from 'styled-components';
import { InfoCardsWrapper, TitleMax2 } from '../../../ApartPage/styles';
import Link from '../../../Link';

export const AdvertRoot = styled(InfoCardsWrapper)`
	grid-area: apartAdvert;
	background-color: #fff;
	gap: 0;
	border-radius: 8px;
	display: grid;
	grid-template-columns: 20% 1fr;
	grid-template-areas:
		'advertText advertButtons'
		'advertDescription advertDescription'
		'showMoreAdvertBtn showMoreAdvertBtn'
		'advertImg advertImg'
		'advertLink advertLink';

	@media (max-width: 960px) {
		grid-template-columns: 100%;
		grid-template-areas:
			'advertText'
			'advertButtons'
			'advertDescription'
			'showMoreAdvertBtn'
			'advertImg'
			'advertLink';
	}
`;

export const Title = styled(TitleMax2)`
	grid-area: advertText;
	align-self: start;

	@media (max-width: 960px) {
		margin: 0 0 10px;
	}
`;

export const AdvertButtonsWrapper = styled.div`
	grid-area: advertButtons;
	width: 100%;
	${({ $withTabs }) =>
		$withTabs
			? css`
					display: grid;
					grid-template-columns: 84px repeat(4, 1fr);
					grid-template-areas: 'tabs . . . .';

					@media (max-width: 600px) {
						grid-template-columns: 1fr 84px;
						grid-template-areas: '. tabs';
					}
			  `
			: css`
					display: flex;
					flex-direction: row;
					justify-content: end;

					@media (max-width: 600px) {
						flex-direction: column;
						justify-content: center;
						align-items: center;
					}
			  `};
`;

export const AdvertTabWrapper = styled.div`
	grid-area: tabs;
	display: flex;
	border-radius: 4px;
	overflow: hidden;
	margin: 0 8px auto 0;
	flex-shrink: 0;

	@media (max-width: 600px) {
		margin: 0 0 0 8px;
	}
`;

export const AdvertTabButton = styled.button`
	position: relative;
	width: 38px;
	height: 38px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ theme }) => theme.lightGrayColor};
	font-size: 0;
	cursor: pointer;
	box-sizing: border-box;
	box-shadow: none;
	border: none;
	transition: background-color 0.3s;

	&:disabled {
		opacity: 0.4;
	}

	&::after {
		content: '';
		position: absolute;
		width: 8px;
		height: 8px;
		top: 50%;
		left: 50%;
		border: 2px solid ${({ theme }) => theme.darkGrayColor};
		transform: translate(-50%, -50%) rotate(45deg);
		transition: border-color 0.3s;

		${({ $isPrev }) =>
			$isPrev &&
			css`
				border-top: none;
				border-right: none;
				margin-left: 2px;
			`}
		${({ $isNext }) =>
			$isNext &&
			css`
				border-left: none;
				border-bottom: none;
				margin-left: -2px;
			`}
	}

	@media (hover: hover) {
		&:not(:disabled) {
			&:hover {
				background-color: ${({ theme }) => theme.lightGrayHoverColor};

				&::after {
					border-color: ${({ theme }) => theme.primaryBlackColor};
				}
			}
		}
	}
`;

export const AdvertInfo = styled.button`
	display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
	flex-direction: column;
	justify-content: center;
	align-items: center;
	box-shadow: none;
	border: none;
	background: transparent;
	padding: 0;
	transition: all 0.3s;

	@media (max-width: 600px) {
		display: ${({ $isHidden }) => ($isHidden ? 'none' : 'grid')};
		background: ${({ theme }) => theme.lightGrayColor};
		grid-template-columns: 1fr 1fr;
		border-radius: 4px;
		justify-content: space-between;
		width: 100%;
		height: 38px;
	}

	@media (max-width: 360px) {
		grid-template-columns: 100%;
	}
`;

const getBtnNumber = (idx, length) => {
	if (length === 1) {
		return '4px 4px 0 0';
	}
	if (idx === 0) {
		return '4px 0 0 0';
	}
	if (idx === length - 1) {
		return '0 4px 0 0';
	}
	return '0';
};

export const AdvertButton = styled.div`
	display: flex;
	box-shadow: none;
	background: ${({ theme }) => theme.lightGrayColor};
	width: 100%;
	height: 38px;
	padding: 0 10px;
	box-sizing: border-box;
	border-top: ${({ theme }) => `2px solid ${theme.lightGrayColor}`};
	border-bottom: ${({ $isActive, theme }) => `2px solid ${$isActive ? theme.primaryColor : theme.lightGrayColor}`};
	color: ${({ $isActive, theme }) => ($isActive ? theme.primaryBlackColor : theme.darkGrayColor)};
	justify-content: center;
	align-items: center;
	cursor: pointer;
	border-radius: ${({ idx, length }) => length && getBtnNumber(idx, length)};

	& > span {
		display: flex;
		align-items: center;
		justify-content: flex-start;
	}

	&:hover {
		background-color: ${({ theme }) => theme.lightGrayHoverColor};
		color: ${({ theme }) => theme.primaryBlackColor};
	}

	@media (max-width: 600px) {
		border: none;
		height: auto;
	}
`;

export const AdvertButtonText = styled.p`
	margin: 0 6px 0 0;
	color: currentColor;
	${({ theme }) => theme.typography.textSemiBoldS};
`;

export const AdvertButtonId = styled.p`
	margin: 0;
	color: currentColor;
	white-space: nowrap;
	${({ theme }) => theme.typography.textSemiBoldS};
`;

export const AdvertPrice = styled.p`
	color: ${({ $isActive, theme }) => ($isActive ? theme.primaryBlackColor : theme.darkGrayColor)};
	padding: 2px 16px;
	box-sizing: border-box;
	${({ theme }) => theme.typography.textMediumS};

	@media (max-width: 600px) {
		padding: 0;
		margin: 0;
	}
`;

export const AdvertDescription = styled.div`
	grid-area: advertDescription;
	width: 100%;
	color: ${({ theme }) => theme.primaryBlackColor};
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: ${({ isOpen }) => (isOpen ? 'unset' : '6')};
	-webkit-box-orient: vertical;
	text-overflow: ellipsis;
	border-top: 1px solid ${({ theme }) => theme.secondaryColor};
	padding-top: 24px;
	${({ theme }) => theme.typography.textS};

	@media (max-width: 600px) {
		margin-top: 24px;
		padding: 0;
		border: none;
		-webkit-line-clamp: ${({ isOpen }) => (isOpen ? 'unset' : '15')};
	}
`;

export const ToggleButton = styled.button`
	grid-area: showMoreAdvertBtn;
	min-width: 78px;
	width: fit-content;
	padding: 5px 0;
	margin: 7px 0;
	box-sizing: border-box;
	text-align: left;
	border: none;
	color: ${({ theme }) => theme.darkGrayColor};
	${({ theme }) => theme.typography.textS};
	background-color: transparent;
	cursor: pointer;
`;

export const DropdownIcon = styled.svg`
	width: 16px;
	height: 16px;
	margin-left: 5px;
	margin-top: 2px;
	fill: none;
	stroke: currentColor;
	transform: rotate(0);
	transition: transform 500ms ease;
	${({ isOpen }) => isOpen && 'transform: rotate(-180deg);'}
`;

export const TooltipAdvertId = styled.div`
	${({ theme }) => theme.typography.buttonS};
`;

export const AdvertLink = styled(Link)`
	grid-area: advertLink;
	margin: 24px 0 0 auto;
	font-weight: 600;
	color: ${({ theme }) => theme.baseGrayColor};

	&:hover {
		color: ${({ theme }) => theme.primaryBlackColor};
	}
`;
